import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import DashboardDetailsDialog from 'features/dashboards/components/DashboardDetailsDialog';
import Menu from 'features/dashboards/components/Menu';
import { listDashboards, createDashboard } from 'features/dashboards/api/dashboardsApi';
import axiosInstance from 'shared/services/api/axiosInstance';
import { errorText } from 'features/dashboards/format';
import { besideClick } from 'shared/components/records/recordsSurface';

function useTeam() {
  const { team } = useParams();
  return team || (typeof window !== 'undefined' ? localStorage.getItem('selectedTeam') : '') || '';
}

/**
 * "Add to dashboard" under a chart in an answer (TRO-469). The card re-runs
 * what produced the chart — the server reads that from the saved answer — so
 * it stays live instead of freezing these rows.
 */
export default function AddToDashboard({ messageId, blockIndex, title = null }) {
  const team = useTeam();
  const [creating, setCreating] = useState(false);
  const [menu, setMenu] = useState(null);
  const [done, setDone] = useState(null);
  const [error, setError] = useState(null);
  // A chart whose query has its own dates would pin as a snapshot: Lucid adds it instead, following the period.
  const [askLucid, setAskLucid] = useState(null);

  const addTo = async (d) => {
    setError(null);
    setAskLucid(null);
    try {
      await axiosInstance.post(`/dashboards/${d.id}/cards/from-answer`, { message_id: messageId, block_index: blockIndex }, { params: { teamId: team } });
      setDone(d);
    } catch (e) {
      if (e?.response?.data?.error === 'fixed_window') setAskLucid(d);
      else setError(errorText(e));
    }
  };

  const open = async (anchor) => {
    setError(null);
    try {
      const list = (await listDashboards(team)).filter((d) => d.can_edit && !d.is_default);
      setMenu({
        anchor,
        items: [
          ...list.map((d) => ({ label: d.name, onClick: () => addTo(d) })),
          ...(list.length ? [null] : []),
          { label: 'New report…', onClick: () => setCreating(true) },
        ],
      });
    } catch (e) { setError(errorText(e)); }
  };

  // Only a saved answer can be pinned: an optimistic row has a client-side key.
  if (!/^\d+$/.test(String(messageId || '')) || !team) return null;
  return (
    <div className="-mt-1 mb-3 flex items-center gap-2 text-[12px]">
      {done ? (
        <span className="text-[color:var(--text-muted)]">
          Added to <Link className="text-[color:var(--text-primary)] underline" to={`/${encodeURIComponent(team)}/reports/${done.id}`} onClick={besideClick(`/${encodeURIComponent(team)}/reports/${done.id}`)}>{done.name}</Link>
        </span>
      ) : (
        <button type="button" onClick={(e) => open(e.currentTarget)}
          className="inline-flex items-center gap-1 text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]">
          <Plus className="h-3.5 w-3.5" /> Add to report
        </button>
      )}
      {error ? <span className="text-[color:var(--feedback-error)]">{error}</span> : null}
      {askLucid ? (
        <span className="text-[color:var(--text-muted)]">
          This chart has its own dates, so it would not follow the report.{' '}
          <button type="button" className="text-[color:var(--text-primary)] underline"
            onClick={() => {
              // Carried on in this conversation, where the chart is.
              window.dispatchEvent(new CustomEvent('trodo:chat:send', { detail: { prompt: `Add the ${title ? `"${title}" ` : ''}chart from your answer above to my "${askLucid.name}" report, following the report's period.` } }));
              setAskLucid(null);
            }}>
            Ask Lucid to add it
          </button>
        </span>
      ) : null}
      {creating ? (
        <DashboardDetailsDialog title="New report" confirmLabel="Create and add" onClose={() => setCreating(false)}
          onSubmit={async (v) => { const { id } = await createDashboard(team, v); await addTo({ id, name: v.name }); }} />
      ) : null}
      {menu ? <Menu anchor={menu.anchor} items={menu.items} onClose={() => setMenu(null)} width={220} /> : null}
    </div>
  );
}
